import { useCallback, useEffect, useState } from 'react';
import AuthLayout from '@/components/common/AuthLayout';
import RegisterForm from '@/components/auth/RegisterForm';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import type { AppError } from '@/features/auth/types';
import { bootstrap, loginGoogle, registerEmail } from '@/features/auth/authThunks';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { captureAffiliateFromSearch } from '@/lib/affiliateReferral';
import { reportSignupOrigin } from '@/lib/signupOrigin';

export default function RegisterPage() {
  const { t } = useTranslation(['register', 'auth', 'common']);
  const dispatch = useAppDispatch();
  const { search } = useLocation();
  const status = useAppSelector((s: RootState) => s.auth.status);
  const lastError = useAppSelector((s: RootState) => s.auth.error);
  const [formError, setFormError] = useState<string | null>(null);
  const loading = status === 'loading';

  useEffect(() => {
    captureAffiliateFromSearch(search, Date.now());
  }, [search]);

  const handleSubmit = useCallback(
    async (data: { email: string; password: string; remember?: boolean }) => {
      setFormError(null);
      try {
        await dispatch(registerEmail(data)).unwrap();
        reportSignupOrigin({ method: 'email', page: 'register' });
        await dispatch(bootstrap());
      } catch (e) {
        const err = e as AppError | undefined;
        setFormError(err?.message ? t(err.message) : t('errors.generic'));
      }
    },
    [dispatch, t],
  );

  const handleGoogle = useCallback(async () => {
    setFormError(null);
    const result = await dispatch(loginGoogle({ mode: 'register' }));
    if (loginGoogle.fulfilled.match(result) && result.meta.isNewUser) {
      reportSignupOrigin({ method: 'google', page: 'register' });
    }
    if (loginGoogle.rejected.match(result)) {
      const err = result.payload;
      if (err?.code === 'cancelled') return;
      setFormError(err?.message ? t(err.message) : t('errors.generic'));
    }
  }, [dispatch, t]);

  return (
    <AuthLayout title={t('register:title')} background={'gradient'}>
      <RegisterForm
        onSubmit={handleSubmit}
        onGoogle={handleGoogle}
        loading={loading}
        formError={formError ?? (lastError ? t(lastError) : null)}
      />
    </AuthLayout>
  );
}
